// ===========================================
// SMS Template Editor Dialog Component
// ===========================================
// Dialog for editing SMS/WhatsApp message templates
// Plain text editor with merge fields, character counter, and preview

import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
  Chip,
  Tooltip,
  Paper,
  Stack,
  LinearProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import PreviewIcon from '@mui/icons-material/Preview';
import SendIcon from '@mui/icons-material/Send';
import { usePermissions } from '../../../hooks/usePermissions';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  getSmsTemplateDetail,
  setCustomSmsBody,
  resetCustomSmsBody,
  sendTestSms,
  SmsMergeField,
} from '../../../api/smsTemplate.api';

interface SmsTemplateEditorDialogProps {
  open: boolean;
  templateKey: string;
  onClose: () => void;
  onSave: () => void;
}

// GSM-7 character counting for real-time typing feedback.
// Mirrors the authoritative logic in apps/backend/src/services/sms/smsTemplate.service.ts.
// Changes to segment calculation MUST be updated in both locations.
const GSM7_BASIC = new Set(
  '@\u00a3$\u00a5\u00e8\u00e9\u00f9\u00ec\u00f2\u00c7\n\u00d8\u00f8\r\u00c5\u00e5\u0394_\u03a6\u0393\u039b\u03a9\u03a0\u03a8\u03a3\u0398\u039e\u00c6\u00e6\u00df\u00c9 !"#\u00a4%&\'()*+,-./0123456789:;<=>?\\u00a1ABCDEFGHIJKLMNOPQRSTUVWXYZ\u00c4\u00d6\u00d1\u00dc\u00a7\u00bfabcdefghijklmnopqrstuvwxyz\u00e4\u00f6\u00f1\u00fc\u00e0'
);
const GSM7_EXTENSION = new Set('|^{}[]~\\€');

function isGsm7(text: string): boolean {
  for (const char of text) {
    if (!GSM7_BASIC.has(char) && !GSM7_EXTENSION.has(char)) return false;
  }
  return true;
}

function getClientCharInfo(text: string) {
  const unicode = !isGsm7(text);
  const charsPerSegment = unicode ? 70 : 160;
  let effectiveLength: number;

  if (unicode) {
    effectiveLength = text.length;
  } else {
    let len = 0;
    for (const char of text) {
      len += GSM7_EXTENSION.has(char) ? 2 : 1;
    }
    effectiveLength = len;
  }

  let smsSegments: number;
  if (effectiveLength === 0) {
    smsSegments = 0;
  } else if (effectiveLength <= charsPerSegment) {
    smsSegments = 1;
  } else {
    const concatCharsPerSegment = unicode ? 67 : 153;
    smsSegments = Math.ceil(effectiveLength / concatCharsPerSegment);
  }

  return { length: text.length, smsSegments, isUnicode: unicode, charsPerSegment, withinWhatsAppLimit: text.length <= 1024 };
}

export default function SmsTemplateEditorDialog({
  open,
  templateKey,
  onClose,
  onSave,
}: SmsTemplateEditorDialogProps) {
  const [body, setBody] = useState('');
  const [bodyError, setBodyError] = useState('');
  const [previewText, setPreviewText] = useState<string | null>(null);
  const [testPhone, setTestPhone] = useState('');
  // #388: only an Admin may send a test to any number; everyone else, to their own mobile.
  const { isOwner } = usePermissions();
  const [testResult, setTestResult] = useState<string | null>(null);
  const [confirmResetOpen, setConfirmResetOpen] = useState(false);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  // Fetch template detail
  const { data: detail, isLoading, error } = useQuery({
    queryKey: ['smsTemplateDetail', templateKey],
    queryFn: () => getSmsTemplateDetail(templateKey),
    enabled: open && !!templateKey,
  });

  // Initialize form when data loads
  useEffect(() => {
    if (detail) {
      setBody(detail.customBody?.body || detail.metadata.defaultBody);
      setBodyError('');
      setPreviewText(null);
      setTestResult(null);
    }
  }, [detail]);

  // Mutations
  const saveMutation = useMutation({
    mutationFn: (newBody: string) => setCustomSmsBody(templateKey, newBody),
    onSuccess: () => onSave(),
    onError: (err: any) => {
      setBodyError(err?.response?.data?.error || err?.message || 'Failed to save template');
    },
  });

  const resetMutation = useMutation({
    mutationFn: () => resetCustomSmsBody(templateKey),
    onSuccess: (data) => {
      setBody(data.defaultBody);
      setBodyError('');
      setPreviewText(null);
    },
  });

  /**
   * Generate a client-side preview by replacing merge fields with example values.
   * This previews the current editor content (including unsaved changes),
   * unlike the server-side preview which only shows the last saved version.
   */
  const generateLocalPreview = () => {
    if (!metadata) return;
    let rendered = body;
    for (const field of metadata.mergeFields) {
      rendered = rendered.replaceAll(field.placeholder, field.example);
    }
    setPreviewText(rendered);
  };

  const testMutation = useMutation({
    mutationFn: (phone: string) => sendTestSms(templateKey, phone),
    onSuccess: (data) => {
      const parts: string[] = [];
      if (data.results.sms) parts.push(`SMS: ${data.results.sms}`);
      if (data.results.whatsapp) parts.push(`WhatsApp: ${data.results.whatsapp}`);
      setTestResult(parts.join(' | '));
    },
    onError: (err: any) => {
      setTestResult(err?.response?.data?.error || 'Failed to send test message');
    },
  });

  const metadata = detail?.metadata;
  const charInfo = getClientCharInfo(body);
  const isModified = detail ? body !== (detail.customBody?.body || detail.metadata.defaultBody) : false;
  const isSaving = saveMutation.isPending || resetMutation.isPending;

  // Validation
  const validateBody = (): boolean => {
    if (body.trim().length === 0) {
      setBodyError('Message body cannot be empty');
      return false;
    }
    if (body.length > 1024) {
      setBodyError('Message body cannot exceed 1024 characters (WhatsApp limit)');
      return false;
    }
    setBodyError('');
    return true;
  };

  // Insert merge field at cursor position
  const insertMergeField = (field: SmsMergeField) => {
    const textarea = bodyRef.current;
    if (!textarea) {
      setBody((prev) => prev + field.placeholder);
      return;
    }

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newBody = body.substring(0, start) + field.placeholder + body.substring(end);
    setBody(newBody);

    // Restore cursor position after the inserted field
    requestAnimationFrame(() => {
      const newCursorPos = start + field.placeholder.length;
      textarea.selectionStart = newCursorPos;
      textarea.selectionEnd = newCursorPos;
      textarea.focus();
    });
  };

  const handleSave = () => {
    if (!validateBody()) return;

    // Check if body is same as default - if so, reset instead
    if (body === detail?.metadata.defaultBody && detail?.isCustomized) {
      resetMutation.mutate();
      return;
    }

    // Only save if different from default
    if (body !== detail?.metadata.defaultBody) {
      saveMutation.mutate(body);
    } else {
      onClose();
    }
  };

  const handleResetClick = () => {
    setConfirmResetOpen(true);
  };

  const handleResetConfirm = () => {
    setConfirmResetOpen(false);
    if (detail?.isCustomized) {
      resetMutation.mutate();
    } else {
      setBody(detail?.metadata.defaultBody || '');
    }
  };

  // Segment color
  const getSegmentColor = (): 'success' | 'warning' | 'error' => {
    if (charInfo.smsSegments <= 1) return 'success';
    if (charInfo.smsSegments <= 3) return 'warning';
    return 'error';
  };

  // WhatsApp progress percentage
  const whatsappProgress = Math.min((charInfo.length / 1024) * 100, 100);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ variant: 'outlined', elevation: 0 }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6">{metadata?.name || 'Edit Template'}</Typography>
            <Typography variant="caption" color="text.secondary">
              {metadata?.description}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {isLoading && (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        )}

        {error && <Alert severity="error">Failed to load template details.</Alert>}

        {metadata && (
          <Stack spacing={3}>
            {/* Merge Fields */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Available Merge Fields
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Click a field to insert it at the cursor position
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {metadata.mergeFields.map((field) => (
                  <Tooltip key={field.key} title={`${field.description} (e.g., ${field.example})`}>
                    <Chip
                      label={field.placeholder}
                      size="small"
                      onClick={() => insertMergeField(field)}
                      variant="outlined"
                      color={field.required ? 'primary' : 'default'}
                      sx={{ cursor: 'pointer', fontFamily: 'monospace', fontSize: '0.75rem' }}
                    />
                  </Tooltip>
                ))}
              </Box>
            </Box>

            <Divider />

            {/* Message Body Editor */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Message Body
              </Typography>
              <TextField
                multiline
                fullWidth
                minRows={4}
                maxRows={10}
                value={body}
                onChange={(e) => {
                  setBody(e.target.value);
                  setBodyError('');
                }}
                inputRef={bodyRef}
                error={!!bodyError}
                helperText={bodyError}
                placeholder="Enter your message..."
                variant="outlined"
                sx={{
                  '& .MuiInputBase-input': {
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '0.9rem',
                    lineHeight: 1.6,
                  },
                }}
              />
            </Box>

            {/* Character Counter */}
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack spacing={1.5}>
                {/* SMS Segments */}
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    SMS Segments
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      label={`${charInfo.smsSegments} segment${charInfo.smsSegments !== 1 ? 's' : ''}`}
                      size="small"
                      color={getSegmentColor()}
                      variant="outlined"
                    />
                    <Typography variant="caption" color="text.secondary">
                      {charInfo.length} chars ({charInfo.isUnicode ? 'Unicode' : 'GSM-7'}, {charInfo.charsPerSegment}/segment)
                    </Typography>
                  </Stack>
                </Box>

                {charInfo.isUnicode && (
                  <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
                    Message contains emoji or special characters. SMS segments use 70 chars instead of 160.
                  </Alert>
                )}

                {/* WhatsApp Limit */}
                <Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                    <Typography variant="body2" color="text.secondary">
                      WhatsApp Limit
                    </Typography>
                    <Typography
                      variant="caption"
                      color={charInfo.withinWhatsAppLimit ? 'text.secondary' : 'error'}
                    >
                      {charInfo.length} / 1,024
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={whatsappProgress}
                    color={charInfo.withinWhatsAppLimit ? 'primary' : 'error'}
                    sx={{ height: 6, borderRadius: 3 }}
                  />
                </Box>
              </Stack>
            </Paper>

            {/* Preview */}
            {previewText !== null && (
              <>
                <Divider />
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Preview (with sample data)
                  </Typography>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      bgcolor: 'grey.50',
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '0.9rem',
                      lineHeight: 1.6,
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {previewText}
                  </Paper>
                </Box>
              </>
            )}

            {/* Test Send */}
            <Divider />
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Send Test Message
              </Typography>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <TextField
                  size="small"
                  placeholder="+61400123456"
                  value={testPhone}
                  onChange={(e) => setTestPhone(e.target.value)}
                  helperText={isOwner ? undefined : 'Your own mobile number (the one on your account)'}
                  sx={{ minWidth: 200 }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<SendIcon />}
                  onClick={() => testMutation.mutate(testPhone)}
                  disabled={!testPhone || testMutation.isPending}
                >
                  {testMutation.isPending ? 'Sending...' : 'Send Test'}
                </Button>
              </Stack>
              {testResult && (
                <Alert severity="info" sx={{ mt: 1 }} variant="outlined">
                  {testResult}
                </Alert>
              )}
            </Box>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Stack direction="row" spacing={1} sx={{ mr: 'auto' }}>
          <Button
            startIcon={<PreviewIcon />}
            onClick={generateLocalPreview}
            size="small"
          >
            Preview
          </Button>
          <Button
            startIcon={<RefreshIcon />}
            onClick={handleResetClick}
            disabled={isSaving}
            size="small"
            color="warning"
          >
            Reset to Default
          </Button>
        </Stack>
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isSaving || !isModified}
        >
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>

      {/* Reset Confirmation Dialog */}
      <Dialog
        open={confirmResetOpen}
        onClose={() => setConfirmResetOpen(false)}
        maxWidth="xs"
        PaperProps={{ variant: 'outlined', elevation: 0 }}
      >
        <DialogTitle>Reset to Default?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will discard your custom message and restore the original default template. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmResetOpen(false)}>Cancel</Button>
          <Button onClick={handleResetConfirm} color="warning" variant="contained">
            Reset
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
